import { useEffect, useState } from 'react';
import type { TrackLyrics } from '@karozik/shared';
import { api } from '@karozik/ui';

// Shared by the screen and the remote: a track's lyrics are asked for once per page.
const cache = new Map<string, Promise<TrackLyrics | null>>();

export function fetchLyrics(eventId: string, trackId: string): Promise<TrackLyrics | null> {
  let p = cache.get(trackId);
  if (!p) {
    p = api<TrackLyrics>(`/api/admin/events/${eventId}/lyrics/${trackId}`).catch(() => {
      cache.delete(trackId);
      return null;
    });
    cache.set(trackId, p);
  }
  return p;
}

const RETRY_MS = 20_000;

/** The lyrics of `trackId` once loaded; undefined while loading, null while LRCLIB is unreachable (asked again every 20 s). */
export function useLyrics(eventId: string, trackId: string | null, enabled: boolean): TrackLyrics | null | undefined {
  const [state, setState] = useState<{ trackId: string; lyrics: TrackLyrics | null } | null>(null);
  useEffect(() => {
    if (!enabled || !trackId) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = () =>
      void fetchLyrics(eventId, trackId).then((lyrics) => {
        if (!alive) return;
        setState({ trackId, lyrics });
        if (!lyrics) timer = setTimeout(load, RETRY_MS);
      });
    load();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [eventId, trackId, enabled]);
  if (!enabled || !trackId) return null;
  return state?.trackId === trackId ? state.lyrics : undefined;
}
